import generate from "@babel/generator";
import * as parser from "@babel/parser";
import traverse from "@babel/traverse";
import * as t from "@babel/types";

interface SelectedElement {
  props: Record<string, any>;
  file: string;
  line: number;
  column: number;
  childText: string;
}

interface ApplyJsxNodeUpdateParams {
  code: string;
  updatedText?: string;
  updatedClassName?: string;
  selectedElement: SelectedElement;
}

/**
 * Apply JSX node updates (text and/or className) to the provided code.
 * This is the frontend version of the backend applyJsxNodeUpdate function.
 *
 * @param params - The parameters object
 * @param params.code - The source code to modify
 * @param params.updatedText - New text content for the element (optional)
 * @param params.updatedClassName - New className for the element (optional)
 * @param params.selectedElement - The element to modify, identified by line/column position
 * @returns The modified code as a string
 */
export function applyJsxNodeUpdate({
  code,
  updatedText,
  updatedClassName,
  selectedElement,
}: ApplyJsxNodeUpdateParams): string {
  const ast = parser.parse(code, {
    sourceType: "module",
    plugins: ["jsx", "typescript"],
  });

  traverse(ast, {
    JSXElement(path) {
      const { node } = path;

      const loc = node.openingElement.loc;
      if (!loc) {
        return;
      }

      // Check for exact match first
      const exactMatch =
        loc.start.line === selectedElement.line &&
        loc.start.column === selectedElement.column;

      // Also check if this element contains our target line and has the expected className
      const containsTargetLine =
        loc.start.line <= selectedElement.line &&
        node.closingElement &&
        node.closingElement.loc &&
        node.closingElement.loc.end.line >= selectedElement.line;

      const hasExpectedClassName =
        selectedElement.props.className &&
        node.openingElement.attributes.some(
          (attr) =>
            attr.type === "JSXAttribute" &&
            attr.name.type === "JSXIdentifier" &&
            attr.name.name === "className" &&
            attr.value &&
            attr.value.type === "StringLiteral" &&
            attr.value.value === selectedElement.props.className,
        );

      const isMatch =
        exactMatch || (containsTargetLine && hasExpectedClassName);

      if (isMatch) {
        if (updatedText) {
          const newTextNode = t.jsxText(updatedText);

          const hasOnlyText = node.children.every(
            (child) =>
              child.type === "JSXText" ||
              child.type === "JSXExpressionContainer",
          );

          if (hasOnlyText) {
            node.children = [newTextNode];
          }
        }

        if (updatedClassName !== undefined) {
          const attributes = node.openingElement.attributes;

          const classNameAttrIndex = attributes.findIndex(
            (attr) =>
              attr.type === "JSXAttribute" &&
              attr.name.type === "JSXIdentifier" &&
              attr.name.name === "className",
          );

          if (updatedClassName.trim() === "") {
            if (classNameAttrIndex !== -1) {
              attributes.splice(classNameAttrIndex, 1);
            }
          } else {
            const newClassNameAttr = t.jsxAttribute(
              t.jsxIdentifier("className"),
              t.stringLiteral(updatedClassName),
            );

            if (classNameAttrIndex !== -1) {
              attributes[classNameAttrIndex] = newClassNameAttr;
            } else {
              attributes.push(newClassNameAttr);
            }
          }
        }

        path.stop();
      }
    },
  });

  const output = generate(
    ast,
    {
      retainLines: true, // Keep original line structure
      compact: false, // Don't compress
      comments: true, // Preserve comments
      minified: false, // Don't minify
      concise: false, // More readable output
      jsescOption: {
        // Conservative settings for SWC compatibility
        quotes: "double",
        minimal: true,
      },
    },
    code,
  );

  return output.code;
}
